import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {MedicalTestListItem, MedicalTestViewerData} from '@shared/models/medical-test.model';
import {FileService} from './file.service';
import {Request} from '@shared/models/request.model';
import {requestStates, radioStudioTypeCodes, requestTypeOrigins} from '@modules/container/request/constants/request.constants';
import {mtestOptionsActions} from '@shared/constants/mtest.constants';
import {PermissionService} from './permission.service';

@Injectable({
  providedIn: 'root'
})
export class MedicalTestService {

  constructor(
    private http: HttpClient,
    private fileService: FileService,
    private permissionService: PermissionService
  ) {
  }

  checkResultsAvailable(radioStudioTypeCode: string, actions: string[]): boolean {
    return this.permissionService.hasAction(mtestOptionsActions.RESULTS, actions)
      || this.permissionService.hasAction(mtestOptionsActions.RESULTS_VIEWER, actions)
      || this.permissionService.hasAction(mtestOptionsActions.RESULTS_VIEWER_URL, actions)
      || this.isStudyWithoutReportAvailable(radioStudioTypeCode, actions);
  }

  isStudyWithoutReportAvailable(radioStudioTypeCode: string, actions: string[]): boolean {
    return radioStudioTypeCode === radioStudioTypeCodes.STUDY_WITHOUT_REPORT
      && this.permissionService.hasAction(mtestOptionsActions.IMAGE, actions)
  }

  async getMedicalTests(
    page: number,
    size: number,
    startDate?: number,
    endDate?: number,
    centerId?: number,
    serviceId?: number,
    requestTypeId?: number
  ): Promise<MedicalTestListItem[]> {

    let httpParams = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (startDate) {
      httpParams = httpParams.set('fromDate', startDate.toString());
    }

    if (endDate) {
      httpParams = httpParams.set('toDate', endDate.toString());
    }

    if (centerId) {
      httpParams = httpParams.set('centerId', centerId.toString());
    }

    if (serviceId) {
      httpParams = httpParams.set('serviceId', serviceId.toString());
    }

    if (requestTypeId) {
      httpParams = httpParams.set('requestTypeId', requestTypeId.toString());
    }

    return this.getMedicalTestUrlHttp(httpParams);
  }

  async getMedicalTestsTop(top: number): Promise<MedicalTestListItem[]> {
    const httpParams = new HttpParams()
      .set('top', top.toString())
      .set('requestTypeOrigin', requestTypeOrigins.MEDICAL_REQUEST)
      .set('requestState', requestStates.DONE);
    return this.getMedicalTestUrlHttp(httpParams);
  }

  getMedicalTestUrlHttp(httpParams: HttpParams): Promise<MedicalTestListItem[]> {
    const url = '/api/v1/request/patient';
    return this.http.get<MedicalTestListItem[]>(url, {params: httpParams}).toPromise();
  }

  async getDownloadDocumentReferral(requestId: number): Promise<void> {
    const url = `/api/v1/request/${requestId}/proof`;
    await this.fileService.downloadPdf(url);
  }

  async getDownloadDocumentResults(requestId: number): Promise<void> {
    const url = `/api/v1/request/${requestId}/results`;
    await this.fileService.downloadPdf(url);
  }

  async getMedicalTest(requestId: number): Promise<Request> {
    const url = `/api/v1/request/${requestId}`;
    return this.http.get<Request>(url).toPromise();
  }

  async getDownloadInformedConsent(requestId: number, icId: number): Promise<void> {
    const url = `/api/v1/request/${requestId}/ic/${icId}`;
    await this.fileService.downloadPdf(url);
  }

  async getDownloadDocumentResultsViewer(requestId: number): Promise<void> {
    const url = `/api/v1/viewer/request/${requestId}/result`;
    await this.fileService.downloadPdf(url);
  }

  async getOpenViewerResultsUrl(requestId: number): Promise<void> {
    const url = `/api/v1/viewer/request/${requestId}/result/url`;
    const viewerUrl = await this.http.get(url, {responseType: 'text'}).toPromise();
    window.location.href = viewerUrl;
  }

  async getOpenViewerDataImage(requestId: number, code: string): Promise<void> {
    const url = `/api/v1/viewer/request/${requestId}/image`;
    let httpParams = new HttpParams();
    if (code) {
      httpParams = httpParams.set('accessionNumber', code);
    }

    const mtestViewerDataUrl = await this.http.get(url, {params: httpParams, responseType: 'text'}).toPromise();
    // this.fileService.openUrl(mtestViewerData.url); // Not working in Safari (iOS) with "Blocking pop-ups"
    window.location.href = mtestViewerDataUrl;
  }

  getShareDocumentResults(requestId: number): Promise<MedicalTestViewerData> {
    const url = `/api/v1/share/request/${requestId}/results`;
    return this.http.post<MedicalTestViewerData>(url, {}).toPromise();
  }

  getShareDocumentResultsViewer(requestId: number): Promise<MedicalTestViewerData> {
    const url = `/api/v1/share/viewer/request/${requestId}/result`;
    return this.http.post<MedicalTestViewerData>(url, {}).toPromise();
  }

  getShareDocumentResultsViewerUrl(requestId: number): Promise<MedicalTestViewerData> {
    const url = `/api/v1/share/viewer/request/${requestId}/result/url`;
    return this.http.post<MedicalTestViewerData>(url, {}).toPromise();
  }

  getShareImageResults(requestId: number, code: string): Promise<MedicalTestViewerData> {
    const url = `/api/v1/share/viewer/request/${requestId}/image`;
    let httpParams = new HttpParams();
    if (code) {
      httpParams = httpParams.set('accessionNumber', code);
    }

    return this.http.post<MedicalTestViewerData>(url, {}, {params: httpParams}).toPromise();
  }
}
